import type { CSSProperties } from 'react';

export const DEFAULT_PROFILE_ACCENT = '#2A7FFF';
export const DEFAULT_PROFILE_ACCENT_LIGHT = '#00CEFC';

const HEX_COLOR_RE = /^#([A-Fa-f0-9]{3}|[A-Fa-f0-9]{6})$/;

export type ProfileResolvedTheme = 'dark' | 'light';
export type ProfileTokenPalette = 'standard' | 'classic';

export type ProfileDesignTokens = {
  accent: string;
  /** CTA fill: the accent, nudged only when its text would miss 4.5:1. */
  accentFill: string;
  /** CTA fill on hover (solid fills, e.g. `--primary-solid-hover`). */
  accentFillHover: string;
  accentStrong: string;
  accentGrad: string;
  accentGradHover: string;
  accentText: string;
  accent12: string;
  accent16: string;
  bg: string;
  surface: string;
  surface2: string;
  border: string;
  text: string;
  muted: string;
  faint: string;
  avatarBorder: string;
};

export function normalizeProfileAccentColor(
  value: string | null | undefined,
): string | null {
  if (!value?.trim()) return null;
  const raw = value.trim();
  const hex = raw.startsWith('#') ? raw : `#${raw}`;
  if (!HEX_COLOR_RE.test(hex)) return null;
  let body = hex.slice(1);
  if (body.length === 3) {
    body = body
      .split('')
      .map((c) => c + c)
      .join('');
  }
  return `#${body.toUpperCase()}`;
}

function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const normalized = normalizeProfileAccentColor(hex) ?? DEFAULT_PROFILE_ACCENT;
  const value = normalized.slice(1);
  return {
    r: parseInt(value.slice(0, 2), 16),
    g: parseInt(value.slice(2, 4), 16),
    b: parseInt(value.slice(4, 6), 16),
  };
}

export function lightenProfileAccentColor(hex: string, amount = 0.35): string {
  const { r, g, b } = hexToRgb(hex);
  const mix = (channel: number) =>
    Math.round(channel + (255 - channel) * amount);
  const toHex = (channel: number) => channel.toString(16).padStart(2, '0');
  return `#${toHex(mix(r))}${toHex(mix(g))}${toHex(mix(b))}`.toUpperCase();
}

export const PROFILE_CTA_INK = '#0A0C0F';
export const PROFILE_CTA_WHITE = '#FFFFFF';
const PROFILE_CTA_MIN_CONTRAST = 4.5;
const PROFILE_CTA_GRADIENT_END = 0.78;
const PROFILE_CTA_MAX_STEPS = 80;

export type ProfileCtaColors = {
  /** Gradient start (the accent, or the nudged accent when `shifted`). */
  fill: string;
  /** Gradient end = fill × 0.78. */
  end: string;
  /** White or ink `#0A0C0F`, whichever has the higher WCAG contrast. */
  text: string;
  /** Hover gradient stops, moved away from the text colour. */
  hover: [string, string];
  /** True when the fill had to move off the raw accent to reach 4.5:1. */
  shifted: boolean;
};

type Rgb = [number, number, number];

function rgbChannels(hex: string): Rgb {
  const { r, g, b } = hexToRgb(hex);
  return [r, g, b];
}

function rgbToHex(channels: Rgb): string {
  return `#${channels
    .map((v) =>
      Math.max(0, Math.min(255, Math.round(v)))
        .toString(16)
        .padStart(2, '0'),
    )
    .join('')}`.toUpperCase();
}

function scaleHex(hex: string, factor: number): string {
  const [r, g, b] = rgbChannels(hex);
  return rgbToHex([r * factor, g * factor, b * factor]);
}

function mixTowardWhite(hex: string, amount: number): string {
  const [r, g, b] = rgbChannels(hex);
  const mix = (v: number) => v + (255 - v) * amount;
  return rgbToHex([mix(r), mix(g), mix(b)]);
}

function relativeLuminance(hex: string): number {
  const linear = (v: number) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  const [r, g, b] = rgbChannels(hex).map(linear) as Rgb;
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG 2.x contrast ratio between two hex colours. */
export function profileContrastRatio(a: string, b: string): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

function worstCtaContrast(text: string, fill: string): number {
  return Math.min(
    profileContrastRatio(text, fill),
    profileContrastRatio(text, scaleHex(fill, PROFILE_CTA_GRADIENT_END)),
  );
}

/**
 * DEV-790 Ink-on-Brand rule for profile CTAs. Text is white or ink, whichever
 * reads better across the whole gradient; if that is still below 4.5:1 the
 * fill moves away from the text (darker under white, lighter under ink) until
 * it passes. Hover moves further away, so it never loses contrast.
 */
export function resolveProfileCtaColors(
  accentColor: string | null | undefined,
): ProfileCtaColors {
  const accent =
    normalizeProfileAccentColor(accentColor) ?? DEFAULT_PROFILE_ACCENT;
  const text =
    worstCtaContrast(PROFILE_CTA_INK, accent) >
    worstCtaContrast(PROFILE_CTA_WHITE, accent)
      ? PROFILE_CTA_INK
      : PROFILE_CTA_WHITE;
  const underWhite = text === PROFILE_CTA_WHITE;

  let fill = accent;
  let steps = 0;
  while (
    worstCtaContrast(text, fill) < PROFILE_CTA_MIN_CONTRAST &&
    steps < PROFILE_CTA_MAX_STEPS
  ) {
    fill = underWhite ? scaleHex(fill, 0.98) : mixTowardWhite(fill, 0.04);
    steps += 1;
  }

  const end = scaleHex(fill, PROFILE_CTA_GRADIENT_END);
  const hoverStop = (stop: string) =>
    underWhite ? scaleHex(stop, 0.92) : mixTowardWhite(stop, 0.1);

  return {
    fill,
    end,
    text,
    hover: [hoverStop(fill), hoverStop(end)],
    shifted: steps > 0,
  };
}

/**
 * Handover §2 — one accent color drives gradient, button text contrast, and tints.
 * Only the CTA fill (`accentFill` / `accentGrad`) may shift off the raw accent
 * (DEV-790); `accent` and the tints always keep the customer's colour.
 * Classic light uses a warmer neutral palette (see profil-classic-*.html).
 */
export function resolveProfileTokens(
  accentColor: string | null | undefined,
  theme: ProfileResolvedTheme,
  palette: ProfileTokenPalette = 'standard',
): ProfileDesignTokens {
  const accent =
    normalizeProfileAccentColor(accentColor) ?? DEFAULT_PROFILE_ACCENT;
  const { r, g, b } = hexToRgb(accent);

  const cta = resolveProfileCtaColors(accent);
  const accentGrad = `linear-gradient(135deg, ${cta.fill}, ${cta.end})`;
  const accentGradHover = `linear-gradient(135deg, ${cta.hover[0]}, ${cta.hover[1]})`;
  const tint = (a: number) => `rgba(${r}, ${g}, ${b}, ${a})`;

  const dark = theme !== 'light';
  const classicLight = !dark && palette === 'classic';

  const neutral = dark
    ? {
        bg: '#0a0c0f',
        surface: '#14171b',
        surface2: 'rgba(255,255,255,0.03)',
        border: 'rgba(255,255,255,0.08)',
        text: '#e7eaee',
        muted: '#7d8692',
        faint: '#6b7480',
      }
    : classicLight
      ? {
          bg: '#faf9f7',
          surface: '#ffffff',
          surface2: '#f1f0ec',
          border: '#e6e3dd',
          text: '#1c1b19',
          muted: '#6b675f',
          faint: '#a39c8c',
        }
      : {
          bg: '#f6f7f9',
          surface: '#ffffff',
          surface2: '#f6f7f9',
          border: '#e8eaee',
          text: '#12161d',
          muted: '#7a8494',
          faint: '#98a1ad',
        };

  return {
    accent,
    accentFill: cta.fill,
    accentFillHover: cta.hover[0],
    accentStrong: cta.end,
    accentGrad,
    accentGradHover,
    accentText: cta.text,
    accent12: tint(0.12),
    accent16: tint(0.16),
    ...neutral,
    avatarBorder: dark || classicLight ? neutral.surface : neutral.bg,
  };
}

export function getProfileAccentCssVars(
  accentColor?: string | null,
  options?: {
    theme?: ProfileResolvedTheme;
    palette?: ProfileTokenPalette;
  },
): CSSProperties {
  const theme = options?.theme ?? 'dark';
  const palette = options?.palette ?? 'standard';
  const tokens = resolveProfileTokens(accentColor, theme, palette);
  const accentLight = lightenProfileAccentColor(tokens.accent);
  const dark = theme !== 'light';

  return {
    // Handover design tokens (`--accent` = brand hex for profile CTAs/modules)
    '--bg': tokens.bg,
    '--surface': tokens.surface,
    '--surface2': tokens.surface2,
    '--border': tokens.border,
    '--text': tokens.text,
    '--muted': tokens.muted,
    '--faint': tokens.faint,
    '--avatar-border': tokens.avatarBorder,
    '--accent': tokens.accent,
    '--accent-strong': tokens.accentStrong,
    '--accent-grad': tokens.accentGrad,
    '--accent-grad-hover': tokens.accentGradHover,
    '--accent-text': tokens.accentText,
    '--accent-12': tokens.accent12,
    '--accent-16': tokens.accent16,
    // Bridge shared Button / form / shadcn utilities used inside public profiles.
    // Outline CTAs use `text-accent-foreground` — without this they inherit the
    // page shell color (often black on a dark profile sheet).
    '--accent-foreground': tokens.text,
    // CTA fills use the contrast-resolved fill (DEV-790); `--accent`, `--ring`,
    // `--profile-accent`, tints and `--chart-5` keep the raw accent.
    '--brand-grad': tokens.accentGrad,
    '--brand-grad-hover': tokens.accentGradHover,
    '--shadow-brand-cta': `0 10px 26px ${tokens.accent16}`,
    '--primary': tokens.accentFill,
    '--ring': tokens.accent,
    '--color-primary': tokens.accentFill,
    '--primary-foreground': tokens.accentText,
    // Text-carrying solid fills (Badge, initials avatars; DEV-793) use the
    // contrast-resolved CTA fill and hover (DEV-790).
    '--primary-solid': tokens.accentFill,
    '--color-primary-solid': tokens.accentFill,
    '--primary-solid-foreground': tokens.accentText,
    '--primary-solid-hover': tokens.accentFillHover,
    '--primary-solid-border': dark
      ? `color-mix(in oklab, ${tokens.accentFill} 65%, white)`
      : `color-mix(in oklab, ${tokens.accentFill} 72%, black)`,
    '--secondary': dark ? 'rgba(255,255,255,0.1)' : tokens.surface2,
    '--secondary-foreground': tokens.text,
    '--sidebar-primary': tokens.accentFill,
    '--sidebar-primary-foreground': tokens.accentText,
    '--chart-5': tokens.accent,
    '--profile-accent': tokens.accent,
    '--profile-accent-light': accentLight,
    '--background': tokens.bg,
    '--foreground': tokens.text,
    '--card': tokens.surface,
    '--card-foreground': tokens.text,
    '--popover': tokens.surface,
    '--popover-foreground': tokens.text,
    '--muted-foreground': tokens.muted,
    // Solid-enough so `dark:bg-input/30` outline buttons read as chips.
    '--input': dark ? 'rgba(255,255,255,0.14)' : tokens.border,
    '--border-color': tokens.border,
    color: tokens.text,
    background: tokens.bg,
  } as CSSProperties;
}

/** Effective accent for display: custom hex or brand default `#2A7FFF`. */
export function resolveProfileAccentColor(accentColor?: string | null): string {
  return normalizeProfileAccentColor(accentColor) ?? DEFAULT_PROFILE_ACCENT;
}

export function resolveProfileThemePreference(
  theme: 'dark' | 'light' | 'auto' | null | undefined,
  prefersDark?: boolean,
): ProfileResolvedTheme {
  if (theme === 'light') return 'light';
  if (theme === 'dark') return 'dark';
  if (theme === 'auto') {
    return prefersDark === false ? 'light' : 'dark';
  }
  return 'dark';
}
